import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Typography,
  Box,
  Paper,
  FormHelperText,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  QueryBuilder as ClockIcon,
  PriorityHigh as PriorityIcon
} from '@mui/icons-material';
import api from '../../config/api';

const SyncOptionsDialog = ({ 
  open, 
  onClose, 
  connector, 
  onSyncQueued,
  queueStatus = { running: [], queued: [], completedToday: 0 }
}) => {
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState({
    dateRange: '3_days',
    startDate: '',
    endDate: '',
    priority: 5
  });

  const dateRangeOptions = [
    { 
      value: '3_days', 
      label: '3 Days', 
      description: 'Sync last 3 days of data (recommended)',
      days: 3 
    },
    { 
      value: '1_week', 
      label: '1 Week', 
      description: 'Sync last 7 days of data',
      days: 7 
    },
    { 
      value: '1_month', 
      label: '1 Month', 
      description: 'Sync last 30 days of data',
      days: 30 
    },
    { 
      value: 'custom', 
      label: 'Custom Range', 
      description: 'Select specific date range',
      days: null 
    }
  ];

  const priorityOptions = [
    { value: 1, label: 'High Priority', description: 'Process before all other jobs', color: 'error' },
    { value: 5, label: 'Normal Priority', description: 'Standard processing order', color: 'primary' },
    { value: 8, label: 'Low Priority', description: 'Process after higher priority jobs', color: 'default' }
  ];

  // Calculate date range based on selection
  const getDateRange = () => {
    if (options.dateRange === 'custom') {
      return {
        start: options.startDate,
        end: options.endDate
      };
    }
    
    const selected = dateRangeOptions.find(opt => opt.value === options.dateRange);
    const today = new Date();
    
    // End date: Yesterday
    const endDate = new Date(today);
    endDate.setDate(today.getDate() - 1);
    
    // Start date: Calculate from today, not from endDate
    const startDate = new Date(today);
    startDate.setDate(today.getDate() - selected.days);
    
    console.log(`[Date Range] ${selected.label}: ${startDate.toISOString().split('T')[0]} to ${endDate.toISOString().split('T')[0]}`);
    
    return {
      start: startDate.toISOString().split('T')[0],
      end: endDate.toISOString().split('T')[0]
    };
  };

  const handleSync = async () => {
    if (options.dateRange === 'custom' && (!options.startDate || !options.endDate)) {
      return;
    }

    setLoading(true);
    
    try {
      const dateRange = getDateRange();
      
      const syncParams = {
        syncType: 'manual',
        dateRangeStart: dateRange.start,
        dateRangeEnd: dateRange.end,
        priority: options.priority
      };

      console.log('=== FRONTEND SYNC DEBUG ===');
      console.log('Triggering sync with params:', syncParams);
      console.log('Connector object:', connector);

      // Try multiple possible connector ID properties
      const connectorId = connector?.id || connector?.['c.id'] || connector?.connector_id;
      
      console.log('Extracted connector ID:', connectorId);
      console.log('Available connector properties:', Object.keys(connector || {}));
      
      if (!connectorId) {
        console.error('Connector ID not found in any expected property:', connector);
        throw new Error('Connector ID is missing - check connector object structure');
      }
      
      console.log('Making request to:', `/api/connectors/${connectorId}/sync`);
      console.log('Request params:', syncParams);
      
      const response = await api.post(
        `/api/connectors/${connectorId}/sync`, 
        syncParams
      );
      
      console.log('Response received:', response.data);

      if (response.data.success) {
        onSyncQueued({
          jobId: response.data.jobId,
          queuePosition: response.data.queuePosition,
          connector: connector.name || connector['c.name'] || 'Unknown Connector',
          dateRange: `${dateRange.start} to ${dateRange.end}`
        });
        onClose();
      }
    } catch (error) {
      console.error('=== FRONTEND SYNC ERROR ===');
      console.error('Error details:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error headers:', error.response?.headers);
      
      // Call onSyncQueued with error for parent to handle
      onSyncQueued({
        error: error.response?.data?.message || error.message || 'Sync failed'
      });
    } finally {
      setLoading(false);
    }
  };

  const isCustomRangeValid = () => {
    if (options.dateRange !== 'custom') return true;
    return options.startDate && options.endDate && options.startDate <= options.endDate;
  };

  const getQueueWaitTime = () => {
    const queuedJobs = queueStatus.queued?.length || 0;
    const runningJobs = queueStatus.running?.length || 0;
    
    if (queuedJobs === 0 && runningJobs < 3) {
      return 'Will start immediately';
    }
    
    const estimatedWait = Math.ceil(queuedJobs / 3) * 20; // Assume 20min average per job
    return `Estimated wait: ${estimatedWait} minutes`;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ScheduleIcon color="primary" />
          <Typography variant="h6">
            Sync Data: {connector?.name || connector?.['c.name'] || 'Unknown Connector'}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Queue data synchronization with custom options
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        {/* Date Range Selection */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Sync Period</InputLabel>
          <Select
            value={options.dateRange}
            onChange={(e) => setOptions({...options, dateRange: e.target.value})}
            label="Sync Period"
          >
            {dateRangeOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                <Box>
                  <Typography variant="body1">{option.label}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.description}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            {options.dateRange !== 'custom' && 
              `Will sync data from ${getDateRange().start} to ${getDateRange().end}`
            }
          </FormHelperText>
        </FormControl>

        {/* Custom Date Range */}
        {options.dateRange === 'custom' && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                value={options.startDate}
                onChange={(e) => setOptions({...options, startDate: e.target.value})}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max: new Date().toISOString().split('T')[0] // Can't select future dates
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="date"
                label="End Date"
                value={options.endDate}
                onChange={(e) => setOptions({...options, endDate: e.target.value})}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: options.startDate,
                  max: new Date().toISOString().split('T')[0]
                }}
              />
            </Grid>
          </Grid>
        )}

        {/* Priority Selection */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Priority</InputLabel>
          <Select
            value={options.priority}
            onChange={(e) => setOptions({...options, priority: e.target.value})}
            label="Priority"
          >
            {priorityOptions.map(priority => (
              <MenuItem key={priority.value} value={priority.value}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PriorityIcon color={priority.color} fontSize="small" />
                  <Box>
                    <Typography variant="body1">{priority.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {priority.description}
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Current Queue Status */}
        <Paper sx={{ p: 2, bgcolor: 'background.default', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <ClockIcon color="primary" fontSize="small" />
            <Typography variant="h6">Current Queue Status</Typography>
          </Box>
          
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">Running Now</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h4" color="success.main">
                  {queueStatus.running?.length || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">/ 3</Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">In Queue</Typography>
              <Typography variant="h4" color="warning.main">
                {queueStatus.queued?.length || 0}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">Completed Today</Typography>
              <Typography variant="h4" color="primary.main">
                {queueStatus.completedToday || 0}
              </Typography>
            </Grid>
          </Grid>

          <Box sx={{ mt: 2, p: 1, bgcolor: 'info.light', borderRadius: 1 }}>
            <Typography variant="body2" color="info.dark">
              <strong>{getQueueWaitTime()}</strong>
            </Typography>
          </Box>
        </Paper>

        {/* Validation Messages */}
        {options.dateRange === 'custom' && !isCustomRangeValid() && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Please select valid start and end dates. Start date must be before or equal to end date.
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSync}
          variant="contained"
          disabled={loading || !isCustomRangeValid()}
          startIcon={loading ? <CircularProgress size={20} /> : <ScheduleIcon />}
        >
          {loading ? 'Queuing...' : 'Queue Sync Job'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SyncOptionsDialog;
